import type { ProductResponseDto } from "@/api/api-client"
import { apiClient } from "@/api/client"
import { useLanguage } from "@/hooks/useLanguage"
import {
  formatPricePerWeightUnit,
  formatTotalWeight,
  getPricePerWeightUnit,
} from "@/lib/productDisplay"
import { cn } from "@/lib/utils"

import { InventoryProductActions } from "./InventoryProductActions"
import type { InventoryLabels, InventoryProductCallbacks } from "./inventory-types"
import { getInventoryMargin, isCriticalStockProduct, isLowStockProduct } from "./inventory-utils"

interface InventoryProductRowProps extends InventoryProductCallbacks {
  product: ProductResponseDto
  labels: InventoryLabels
  isHighlighted: boolean
  isDeleting: boolean
}

export function InventoryProductRow({
  product,
  labels,
  isHighlighted,
  isDeleting,
  onManageBatches,
  onEdit,
  onDelete,
}: InventoryProductRowProps) {
  const isLowStock = isLowStockProduct(product)
  const isCritical = isCriticalStockProduct(product)
  const { t } = useLanguage()
  const perWeightUnitPrice = getPricePerWeightUnit(product)
  const hasWeight = !!product.weight && product.weight > 0

  return (
    <div
      id={`inventory-product-${product.id}`}
      className={cn(
        "group grid grid-cols-12 items-center gap-4 border-[1px] bg-card p-2 shadow-sm transition-all hover:border-accent",
        isHighlighted ? "border-accent ring-1 ring-accent" : "border-border"
      )}
    >
      <div className="col-span-1 aspect-square overflow-hidden border-[1px] border-foreground/5 bg-muted">
        {product.primaryImageUrl && (
          <img
            src={apiClient.ensureAbsUrl(product.primaryImageUrl)}
            alt=""
            className="h-full w-full object-cover transition-all duration-500"
          />
        )}
      </div>
      <div className="col-span-3">
        <p className="mb-1 font-display text-xl leading-none tracking-wide transition-colors group-hover:text-accent">
          {product.name}
        </p>
        <p className="font-mono text-xs font-black uppercase text-muted-foreground">
          {product.sku || "NO-SKU"}
        </p>
      </div>
      <div className="col-span-2 text-center">
        <p className="mb-1 text-xs font-black uppercase tracking-tighter text-muted-foreground">
          {labels.price}
        </p>
        {hasWeight ? (
          <>
            <p className="font-black tabular-nums text-foreground">
              {formatPricePerWeightUnit(perWeightUnitPrice, product.weightUnit, {
                isTaxable: !!product.isTaxable,
              })}
            </p>
            <p className="mt-0.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {product.weight}
              {product.weightUnit}/{t.units.perUnit}
            </p>
          </>
        ) : (
          <p className="font-black tabular-nums">${product.price}</p>
        )}
      </div>
      <div className="col-span-2 text-center">
        <p className="mb-1 text-xs font-black uppercase tracking-tighter text-muted-foreground">
          STOCK
        </p>
        <p
          className={cn(
            "font-display text-2xl leading-none",
            isCritical ? "text-destructive" : isLowStock ? "text-amber-600" : "text-foreground"
          )}
        >
          {product.stockQuantity}
        </p>
        {hasWeight && (
          <p className="mt-0.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {formatTotalWeight(
              product.stockQuantity || 0,
              product.weight,
              product.weightUnit,
              t.units.total
            )}
          </p>
        )}
      </div>
      <div className="col-span-2 text-center">
        <p className="mb-1 text-xs font-black uppercase tracking-tighter text-muted-foreground">
          {labels.margin}
        </p>
        <p className="text-xs font-bold text-emerald-600">
          {getInventoryMargin(product).toFixed(1)}%
        </p>
      </div>
      <InventoryProductActions
        product={product}
        labels={labels}
        variant="dense"
        isDeleting={isDeleting}
        onManageBatches={onManageBatches}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    </div>
  )
}
